import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { Button, IButtonProps } from "@/components/Button";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { deleteFavouriteJobRequest, setFavouriteJobRequest } from "@/data/job-requests";
import { JobRequestListDto } from "@/data/job-requests/types/JobRequest";
import { formatMultipleLocations } from "@/data/locations";

interface IJobRequestFavouriteButtonProps extends IButtonProps {
  record: JobRequestListDto;
  onUpdate?: (record: JobRequestListDto) => void;
}

export const JobRequestFavouriteButton: FC<IJobRequestFavouriteButtonProps> = ({
  record,
  onUpdate,
  ...buttonProps
}) => {
  const User = useContext(UserContext);
  const companyId = User.contextCompany?.id;
  const { t } = useTranslation("session");
  const { sendEvent } = useAnalytics();

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        if (record.favourite) {
          await deleteFavouriteJobRequest({ jobRequestId: record.id, companyId });
        } else {
          await setFavouriteJobRequest({ jobRequestId: record.id, companyId });

          sendEvent({
            type: "save_job",
            id: record.id,
            name: record.title!,
            location: formatMultipleLocations(t, record.locations, record.locations?.length),
          });
        }

        if (onUpdate) {
          onUpdate({ ...record, favourite: !record.favourite });
        }
      } catch (error) {
        // TODO: handle errors
      }
    },
    [record, onUpdate, companyId, sendEvent, t],
  );

  return (
    <Button
      variant="card-bottom"
      icon="bookmark"
      iconColor={record.favourite ? "default" : undefined}
      onClick={handleClick}
      {...buttonProps}>
      <Text>{t(record.favourite ? "JOB_REQUESTS.DETAIL.SAVED_JOB" : "JOB_REQUESTS.DETAIL.SAVE_JOB")}</Text>
    </Button>
  );
};
